import React, { useState } from 'react'
import { Dropdown, type DropdownOption } from '../ui/Dropdown.js'
import { useThemeTokens } from '../../themes'

// Sample data for examples
const simpleOptions: DropdownOption[] = [
  { id: 1, label: 'Option 1', value: 'option1' },
  { id: 2, label: 'Option 2', value: 'option2' },
  { id: 3, label: 'Option 3', value: 'option3' },
]

const mediaOptions: DropdownOption[] = [
  { 
    id: 1, 
    label: 'Github', 
    value: 'github',
    icon: '🐙',
    description: 'Code repository hosting'
  },
  { 
    id: 2, 
    label: 'Instagram', 
    value: 'instagram',
    icon: '📷',
    description: 'Photo and video sharing'
  },
  { 
    id: 3, 
    label: 'LinkedIn', 
    value: 'linkedin',
    icon: '💼',
    description: 'Professional networking'
  },
  { 
    id: 4, 
    label: 'Facebook', 
    value: 'facebook',
    icon: '👥',
    description: 'Social networking'
  },
  { 
    id: 5, 
    label: 'Twitter', 
    value: 'twitter',
    icon: '🐦',
    description: 'Microblogging platform'
  },
]

const statusOptions: DropdownOption[] = [
  { 
    id: 1, 
    label: 'Active', 
    value: 'active',
    icon: '🟢'
  },
  { 
    id: 2, 
    label: 'Pending', 
    value: 'pending',
    icon: '🟡'
  },
  { 
    id: 3, 
    label: 'Inactive', 
    value: 'inactive',
    icon: '🔴'
  },
  { 
    id: 4, 
    label: 'Disabled', 
    value: 'disabled',
    icon: '⚫',
    disabled: true
  },
]

export const DropdownExamples: React.FC = () => {
  const [simpleValue, setSimpleValue] = useState<string>()
  const [mediaValue, setMediaValue] = useState<string>()
  const [statusValue, setStatusValue] = useState<string>()
  const [searchableValue, setSearchableValue] = useState<string>()
  
  const theme = useThemeTokens()

  const containerStyles: React.CSSProperties = {
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
    fontFamily: theme.typography.fontFamily,
  }

  const sectionStyles: React.CSSProperties = {
    marginBottom: theme.spacing.xl,
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.lg,
    border: `1px solid ${theme.colors.border}`,
  }

  const titleStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  }

  const subtitleStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.sm,
  }

  const descriptionStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.md,
  }

  return (
    <div style={containerStyles}>
      <h1 style={titleStyles}>Dropdown Component Examples</h1>
      <p style={descriptionStyles}>
        Demonstration of the reusable, themeable dropdown component with various configurations.
      </p>

      {/* Simple Dropdown */}
      <div style={sectionStyles}>
        <h2 style={subtitleStyles}>Basic Dropdown</h2>
        <p style={descriptionStyles}>
          A simple dropdown with basic options, no search functionality.
        </p>
        <div style={{ maxWidth: '300px' }}>
          <Dropdown
            options={simpleOptions}
            value={simpleValue}
            onSelect={(option) => setSimpleValue(option.value as string)}
            placeholder="Select an option..."
            label="Basic Selection"
          />
        </div>
        {simpleValue && (
          <p style={{ ...descriptionStyles, marginTop: theme.spacing.sm }}>
            Selected: <strong>{simpleValue}</strong>
          </p>
        )}
      </div>

      {/* Media Dropdown (like your reference image) */}
      <div style={sectionStyles}>
        <h2 style={subtitleStyles}>Media Selector (Reference Design)</h2>
        <p style={descriptionStyles}>
          Dropdown with icons and descriptions, matching your reference design.
        </p>
        <div style={{ maxWidth: '400px' }}>
          <Dropdown
            options={mediaOptions}
            value={mediaValue}
            onSelect={(option) => setMediaValue(option.value as string)}
            placeholder="Select Media"
            label="Social Media Platform"
          />
        </div>
        {mediaValue && (
          <p style={{ ...descriptionStyles, marginTop: theme.spacing.sm }}>
            Selected: <strong>{mediaOptions.find(opt => opt.value === mediaValue)?.label}</strong>
          </p>
        )}
      </div>

      {/* Status Dropdown */}
      <div style={sectionStyles}>
        <h2 style={subtitleStyles}>Status Dropdown</h2>
        <p style={descriptionStyles}>
          Dropdown with status indicators and disabled options.
        </p>
        <div style={{ maxWidth: '300px' }}>
          <Dropdown
            options={statusOptions}
            value={statusValue}
            onSelect={(option) => setStatusValue(option.value as string)}
            placeholder="Select status..."
            label="Project Status"
            required
          />
        </div>
        {statusValue && (
          <p style={{ ...descriptionStyles, marginTop: theme.spacing.sm }}>
            Selected: <strong>{statusOptions.find(opt => opt.value === statusValue)?.label}</strong>
          </p>
        )}
      </div>

      {/* Searchable Dropdown */}
      <div style={sectionStyles}>
        <h2 style={subtitleStyles}>Searchable Dropdown</h2>
        <p style={descriptionStyles}>
          Type-ahead search functionality with filtering.
        </p>
        <div style={{ maxWidth: '400px' }}>
          <Dropdown
            options={mediaOptions}
            value={searchableValue}
            onSelect={(option) => setSearchableValue(option.value as string)}
            placeholder="Type to search..."
            label="Searchable Media"
            searchable
          />
        </div>
        {searchableValue && (
          <p style={{ ...descriptionStyles, marginTop: theme.spacing.sm }}>
            Selected: <strong>{mediaOptions.find(opt => opt.value === searchableValue)?.label}</strong>
          </p>
        )}
      </div>

      {/* Custom Rendered Options */}
      <div style={sectionStyles}>
        <h2 style={subtitleStyles}>Custom Rendered Options</h2>
        <p style={descriptionStyles}>
          Custom rendering for both options and selected items.
        </p>
        <div style={{ maxWidth: '400px' }}>
          <Dropdown
            options={mediaOptions}
            value={mediaValue}
            onSelect={(option) => setMediaValue(option.value as string)}
            placeholder="Select platform..."
            label="Custom Rendered"
            renderOption={(option, isHighlighted) => (
              <div style={{
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: isHighlighted ? theme.colors.primary + '15' : 'transparent',
                borderRadius: theme.borderRadius.md,
                margin: '4px',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}>
                <span style={{ fontSize: '20px' }}>{option.icon}</span>
                <div>
                  <div style={{ 
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.text 
                  }}>
                    {option.label}
                  </div>
                  <div style={{ 
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.textSecondary 
                  }}>
                    {option.description}
                  </div>
                </div>
              </div>
            )}
            renderSelected={(option) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>{option.icon}</span>
                <span style={{ fontWeight: theme.typography.fontWeight.medium }}>
                  {option.label}
                </span>
              </div>
            )}
          />
        </div>
      </div>

      {/* Error State */}
      <div style={sectionStyles}>
        <h2 style={subtitleStyles}>Error State</h2>
        <p style={descriptionStyles}>
          Dropdown with error styling and message.
        </p>
        <div style={{ maxWidth: '300px' }}>
          <Dropdown
            options={simpleOptions}
            value={undefined}
            onSelect={() => {}}
            placeholder="This field has an error..."
            label="Field with Error"
            required
            error="This field is required"
          />
        </div>
      </div>

      {/* Disabled State */}
      <div style={sectionStyles}>
        <h2 style={subtitleStyles}>Disabled State</h2>
        <p style={descriptionStyles}>
          Dropdown in disabled state.
        </p>
        <div style={{ maxWidth: '300px' }}>
          <Dropdown
            options={simpleOptions}
            value="option2"
            onSelect={() => {}}
            placeholder="Disabled dropdown..."
            label="Disabled Field"
            disabled
          />
        </div>
      </div>
    </div>
  )
} 